import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { setCardListContext } from "@/lib/cardContext";
import { ArrowLeft, Calendar, Hash, Check, Search, Trophy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";

const RARITY_PILL = {
  "Common": "bg-slate-100 text-slate-500",
  "Uncommon": "bg-emerald-50 text-emerald-700",
  "Rare": "bg-amber-50 text-amber-700",
  "Rare Holo": "bg-amber-100 text-amber-800",
  "Rare Ultra": "bg-rose-50 text-rose-700",
  "Rare Secret": "bg-fuchsia-50 text-fuchsia-700",
  "Rare Holo GX": "bg-rose-50 text-rose-700",
  "Rare Holo EX": "bg-rose-50 text-rose-700",
  "Rare Holo V": "bg-rose-50 text-rose-700",
  "Rare Holo VMAX": "bg-rose-50 text-rose-700",
  "Rare Rainbow": "bg-fuchsia-50 text-fuchsia-700",
  "Promo": "bg-blue-50 text-blue-700",
};

// Grid presets — number of columns at xl breakpoint. Responsive scale-down for smaller screens.
const GRID_PRESETS = {
  4:  { classes: "grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4", compact: false },
  6:  { classes: "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4", compact: false },
  8:  { classes: "grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-3", compact: true },
  10: { classes: "grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-9 xl:grid-cols-10 gap-2", compact: true },
};
const DENSITY_OPTIONS = [4, 6, 8, 10];

function pickCardPrice(c) {
  const cm = c?.cardmarket?.prices || {};
  const v = cm.trendPrice || cm.averageSellPrice || cm.lowPrice;
  if (v && v > 0) return { value: v, currency: "EUR" };
  const tp = c?.tcgplayer?.prices || {};
  for (const f of ["normal", "holofoil", "reverseHolofoil", "1stEditionHolofoil"]) {
    const m = tp[f]?.market;
    if (m && m > 0 && m < 999) return { value: m, currency: "USD" };
  }
  return null;
}

export default function SetDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { format, convert } = useCurrency();
  const [set, setSet] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ownedIds, setOwnedIds] = useState(new Set());
  const [busyId, setBusyId] = useState(null);
  const [q, setQ] = useState("");
  const [rarityFilter, setRarityFilter] = useState("all");
  const [ownedOnly, setOwnedOnly] = useState(false);
  // Grid density: user picks how many cards per row on desktop (4/6/8/10).
  // 4-6 = "standard" tiles with names + prices; 8-10 = "compact" tiles for fast browsing.
  // Persisted in localStorage. Legacy "standard"/"compact" values are migrated.
  const [colsPerRow, setColsPerRow] = useState(() => {
    if (typeof window === "undefined") return 6;
    const stored = window.localStorage.getItem("cartonomie_grid_cols");
    const legacy = window.localStorage.getItem("cartonomie_grid_density");
    if (stored && GRID_PRESETS[Number(stored)]) return Number(stored);
    if (legacy === "compact") return 10;
    if (legacy === "standard") return 6;
    return 6;
  });
  useEffect(() => {
    try { window.localStorage.setItem("cartonomie_grid_cols", String(colsPerRow)); } catch { /* ignore */ }
  }, [colsPerRow]);
  const preset = GRID_PRESETS[colsPerRow] || GRID_PRESETS[6];
  const isCompact = preset.compact;

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/sets/${id}`),
      api.get(`/sets/${id}/cards`, { params: { pageSize: 500 } }),
    ])
      .then(([sr, cr]) => {
        setSet(sr.data);
        setCards(cr.data.data || []);
      })
      .finally(() => setLoading(false));
  }, [id, i18n.language]);

  useEffect(() => {
    if (!user) {
      setOwnedIds(new Set());
      return;
    }
    api.get("/collection/owned-ids", { params: { set_id: id, status: "owned" } })
      .then((r) => setOwnedIds(new Set(r.data.card_ids || [])))
      .catch(() => {});
  }, [user, id]);

  const rarities = useMemo(() => {
    const s = new Set();
    cards.forEach((c) => c.rarity && s.add(c.rarity));
    return ["all", ...Array.from(s)];
  }, [cards]);

  const visibleCards = useMemo(() => {
    let list = cards;
    if (q) {
      const ql = q.toLowerCase();
      list = list.filter((c) => (c.name || "").toLowerCase().includes(ql) || (c.number || "").includes(q));
    }
    if (rarityFilter !== "all") list = list.filter((c) => c.rarity === rarityFilter);
    if (ownedOnly) list = list.filter((c) => ownedIds.has(c.id));
    return list;
  }, [cards, q, rarityFilter, ownedOnly, ownedIds]);

  const ownedCountInSet = useMemo(() => {
    return cards.filter((c) => ownedIds.has(c.id)).length;
  }, [cards, ownedIds]);

  const setTotalValue = useMemo(() => {
    // sum of all card values in the set, in active currency (EUR base)
    let sum = 0;
    cards.forEach((c) => {
      const p = pickCardPrice(c);
      if (p) sum += convert(p.value, p.currency) || 0;
    });
    return sum;
  }, [cards, convert]);

  const ownedValue = useMemo(() => {
    let sum = 0;
    cards.forEach((c) => {
      if (!ownedIds.has(c.id)) return;
      const p = pickCardPrice(c);
      if (p) sum += convert(p.value, p.currency) || 0;
    });
    return sum;
  }, [cards, ownedIds, convert]);

  const topMostExpensive = useMemo(() => {
    // top 5 cards in this set by price (in EUR base for sorting)
    return [...cards]
      .map((c) => {
        const p = pickCardPrice(c);
        return p ? { card: c, valueEur: convert(p.value, p.currency) || 0, currency: p.currency, original: p.value } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.valueEur - a.valueEur)
      .slice(0, 5);
  }, [cards, convert]);

  const toggleOwn = async (e, card) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error(t("set_detail.login_required", "Connectez-vous pour gérer votre collection."));
      return;
    }
    setBusyId(card.id);
    try {
      const res = await api.post("/collection/toggle", {
        card_id: card.id,
        status: "owned",
        quantity: 1,
        language: "en",
        condition: "near_mint",
      });
      setOwnedIds((prev) => {
        const next = new Set(prev);
        if (res.data.owned) next.add(card.id);
        else next.delete(card.id);
        return next;
      });
      toast.success(
        res.data.owned
          ? t("set_detail.added_owned", "{{name}} ajoutée à votre collection", { name: card.name })
          : t("set_detail.removed_owned", "{{name}} retirée de votre collection", { name: card.name })
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(typeof msg === "string" ? msg : "Erreur");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="page-enter max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
      {set && (
        <Seo
          title={`${set.name} · ${set.total || ""} cartes · prix Pokémon TCG`}
          description={`Toutes les cartes de ${set.name} (${set.series || "Pokémon TCG"}). Prix multi-langues, checklist complète, ajout direct à votre collection.`}
          image={set.images?.logo}
          url={`${window.location.origin}/sets/${set.id}`}
          keywords={`${set.name}, ${set.series || ""}, Pokémon TCG, prix cartes, collection`}
        />
      )}

      <Link
        to="/sets"
        data-testid="set-detail-back-btn"
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#E11D48] mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> {t("set_detail.back")}
      </Link>

      {loading ? (
        <div className="text-center text-slate-400 py-20 text-sm">{t("common.loading")}</div>
      ) : set ? (
        <>
          {/* Header */}
          <div className="flat-card p-6 md:p-8 mb-8 flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              {set.images?.logo && (
                <img src={set.images.logo} alt={set.name} className="h-20 md:h-24 object-contain" />
              )}
              <div>
                {set.series && (
                  <div className="text-xs font-semibold text-[#E11D48] uppercase tracking-wider mb-1">
                    {set.series}
                  </div>
                )}
                <h1 className="font-heading font-extrabold text-3xl md:text-5xl tracking-tight text-slate-900">
                  {set.name}
                </h1>
                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-slate-500">
                  {set.releaseDate && (
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" /> {set.releaseDate}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5">
                    <Hash className="w-3.5 h-3.5" /> {set.total} {t("set_detail.cards")}
                  </span>
                  {set.ptcgoCode && <span className="font-mono text-xs">· {set.ptcgoCode}</span>}
                </div>
              </div>
            </div>
            {/* Right side: Set total value (always visible) + user progress (if logged in) */}
            <div className="flex items-center gap-6 md:gap-8">
              <div className="text-right">
                <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                  {t("set_detail.set_value", "Valeur du set")}
                </div>
                <div className="font-heading font-extrabold text-3xl text-slate-900 mt-1">
                  {format(setTotalValue, "EUR")}
                </div>
                <div className="text-[11px] text-slate-400 mt-1">
                  {cards.length} {t("set_detail.cards")}
                </div>
              </div>

              {user && (
                <div className="text-right border-l border-slate-200 pl-6 md:pl-8">
                  <div className="text-xs uppercase tracking-wider text-[#E11D48] font-semibold">
                    {t("set_detail.your_value", "Votre collection")}
                  </div>
                  <div className="font-heading font-extrabold text-3xl text-[#E11D48] mt-1">
                    {format(ownedValue, "EUR")}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {ownedCountInSet}<span className="text-slate-300">/{cards.length || set.total}</span> · {setTotalValue > 0 ? Math.round((ownedValue / setTotalValue) * 100) : 0}%
                  </div>
                  <div className="w-40 h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#E11D48] to-[#F59E0B] transition-all"
                      style={{ width: `${cards.length ? Math.min(100, (ownedCountInSet / cards.length) * 100) : 0}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Pre-release banner — shown when the set is announced but cards aren't yet indexed */}
          {set.prerelease && cards.length === 0 && (
            <div
              data-testid="prerelease-banner"
              className="mb-6 rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 to-white p-4 md:p-5 flex items-start gap-4"
            >
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-amber-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-heading font-black uppercase tracking-widest text-amber-800 bg-amber-200/70 px-2 py-0.5 rounded-full">
                    {t("set_detail.prerelease_badge", "Pré-sortie")}
                  </span>
                  <h3 className="font-heading font-bold text-base text-slate-900">
                    {t("set_detail.prerelease_title", "Extension annoncée — cartes bientôt disponibles")}
                  </h3>
                </div>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                  {t(
                    "set_detail.prerelease_desc",
                    "Cette extension est officiellement annoncée pour le {{date}}. Le catalogue complet sera importé automatiquement dès sa publication par nos sources de données.",
                    { date: set.releaseDate || "—" }
                  )}
                </p>
              </div>
            </div>
          )}
          {topMostExpensive.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-amber-500" />
                <h3 className="font-heading font-bold text-lg text-slate-900">
                  {t("set_detail.top_expensive", "Top 5 cartes les plus chères de cette série")}
                </h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {topMostExpensive.map((entry, i) => {
                  const c = entry.card;
                  const owned = ownedIds.has(c.id);
                  const onClick = () => setCardListContext(
                    topMostExpensive.map((e) => e.card.id),
                    { url: `/sets/${id}`, label: set?.name },
                  );
                  return (
                    <Link
                      key={c.id}
                      to={`/cards/${c.id}`}
                      onClick={onClick}
                      data-testid={`top-expensive-${c.id}`}
                      className={`flat-card overflow-hidden group block hover:-translate-y-1 hover:shadow-lg transition-all relative ${owned ? "ring-2 ring-[#E11D48]/40" : ""}`}
                    >
                      <div className={`absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
                        i === 0 ? "bg-amber-400 text-amber-900" :
                        i === 1 ? "bg-slate-300 text-slate-700" :
                        i === 2 ? "bg-amber-700 text-amber-50" :
                        "bg-slate-100 text-slate-500"
                      }`}>
                        #{i + 1}
                      </div>
                      {owned && (
                        <div className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full bg-[#E11D48] text-white flex items-center justify-center">
                          <Check className="w-3.5 h-3.5" strokeWidth={3} />
                        </div>
                      )}
                      <div className="aspect-[2.5/3.5] bg-slate-50 overflow-hidden">
                        {c.images?.small ? (
                          <img src={c.images.small} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        ) : <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">—</div>}
                      </div>
                      <div className="p-2.5">
                        <div className="font-heading font-bold text-xs text-slate-900 truncate">{c.name}</div>
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-[10px] text-slate-400 font-mono">#{c.number}</span>
                          <span className="font-bold text-sm text-amber-600">{format(entry.original, entry.currency)}</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="relative flex-1 min-w-[200px] sm:max-w-xs">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                data-testid="set-cards-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("set_detail.filter_placeholder", "Filtrer par nom ou n°")}
                className="w-full pl-9"
              />
            </div>
            <select
              data-testid="rarity-filter"
              value={rarityFilter}
              onChange={(e) => setRarityFilter(e.target.value)}
              className="text-sm"
            >
              {rarities.map((r) => (
                <option key={r} value={r}>
                  {r === "all" ? t("set_detail.all_rarities", "Toutes raretés") : r}
                </option>
              ))}
            </select>
            {user && (
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                <input
                  data-testid="owned-only-toggle"
                  type="checkbox"
                  checked={ownedOnly}
                  onChange={(e) => setOwnedOnly(e.target.checked)}
                  className="w-4 h-4 accent-[#E11D48]"
                />
                {t("set_detail.owned_only", "Possédées uniquement")}
              </label>
            )}
            {/* Density switcher — pick number of cards per row on desktop */}
            <div
              className="ml-auto flex items-center gap-1 bg-slate-100 rounded-xl p-0.5"
              data-testid="grid-density-switcher"
              role="radiogroup"
              aria-label={t("set_detail.density_label", "Cartes par ligne")}
            >
              <span className="hidden sm:inline pl-2 pr-1 text-[10px] font-mono uppercase tracking-wider text-slate-500">
                {t("set_detail.density_label", "Par ligne")}
              </span>
              {DENSITY_OPTIONS.map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={colsPerRow === n}
                  onClick={() => setColsPerRow(n)}
                  data-testid={`grid-cols-${n}`}
                  aria-label={t("set_detail.density_option", "{{n}} cartes par ligne", { n })}
                  title={t("set_detail.density_option", "{{n}} cartes par ligne", { n })}
                  className={`min-w-[28px] px-2 py-1 rounded-lg text-xs font-heading font-bold transition ${
                    colsPerRow === n
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="text-sm text-slate-400">
              {visibleCards.length} / {cards.length}
            </div>
          </div>

          {/* Cards grid */}
          {visibleCards.length === 0 ? (
            <div className="text-center text-slate-400 py-20 text-sm">
              {t("set_detail.no_cards", "Aucune carte ne correspond à votre filtre.")}
            </div>
          ) : (
            <div
              data-testid="cards-grid"
              data-density={isCompact ? "compact" : "standard"}
              data-cols={colsPerRow}
              className={preset.classes}
            >
              {visibleCards.map((c, i) => {
                const owned = ownedIds.has(c.id);
                const price = pickCardPrice(c);
                const rarityCls = RARITY_PILL[c.rarity] || "bg-slate-100 text-slate-500";
                return (
                  <motion.div
                    key={c.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: Math.min(i, 24) * 0.02 }}
                    className="relative"
                  >
                    {/* Ownership checkbox — absolute overlay (hidden in compact mode for cleaner browsing) */}
                    {!isCompact && (
                      <button
                        type="button"
                        data-testid={`own-toggle-${c.id}`}
                        onClick={(e) => toggleOwn(e, c)}
                        disabled={busyId === c.id}
                        title={owned ? t("set_detail.owned_yes", "Vous possédez cette carte") : t("set_detail.owned_no", "Cocher pour ajouter à votre collection")}
                        className={`absolute top-2 right-2 z-10 w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all shadow-md ${
                          owned
                            ? "bg-[#E11D48] border-[#E11D48] text-white scale-100"
                            : "bg-white/90 border-slate-200 text-transparent hover:border-[#E11D48] hover:text-[#E11D48] hover:scale-110"
                        } ${busyId === c.id ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
                      >
                        <Check className="w-4 h-4" strokeWidth={3} />
                      </button>
                    )}
                    {isCompact && owned && (
                      <div className="absolute top-1 right-1 z-10 w-4 h-4 rounded-full bg-[#E11D48] flex items-center justify-center pointer-events-none shadow">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={4} />
                      </div>
                    )}

                    <Link
                      to={`/cards/${c.id}`}
                      onClick={() => setCardListContext(
                        visibleCards.map((vc) => vc.id),
                        { url: `/sets/${id}`, label: set?.name },
                      )}
                      data-testid={`card-tile-${c.id}`}
                      className={`block flat-card overflow-hidden group transition-all hover:-translate-y-1 hover:shadow-lg ${
                        owned ? "ring-2 ring-[#E11D48]/40" : ""
                      }`}
                    >
                      <div className="aspect-[2.5/3.5] bg-slate-50 overflow-hidden">
                        {c.images?.small ? (
                          <img
                            src={c.images.small}
                            alt={c.name}
                            loading="lazy"
                            className={`w-full h-full object-cover transition-transform group-hover:scale-105 ${
                              !owned && user ? "opacity-95" : ""
                            }`}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">
                            no image
                          </div>
                        )}
                      </div>
                      {!isCompact && (
                        <div className="p-3">
                          <div className="font-heading font-bold text-sm leading-tight text-slate-900 truncate">
                            {c.name}
                          </div>
                          <div className="flex justify-between items-center mt-1.5">
                            <span className="text-[11px] text-slate-400 font-mono">
                              #{c.number}/{set.printedTotal || set.total}
                            </span>
                            {c.rarity && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wide ${rarityCls}`}>
                                {c.rarity.replace("Rare ", "")}
                              </span>
                            )}
                          </div>
                          <div className="mt-2 flex items-center justify-between text-sm">
                            {price ? (
                              <span className="font-bold text-slate-900">
                                {format(price.value, price.currency)}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-300">
                                {t("set_detail.no_price", "—")}
                              </span>
                            )}
                            {owned && (
                              <span className="text-[10px] uppercase tracking-wider font-bold text-[#E11D48]">
                                {t("set_detail.owned_badge", "Possédée")}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      {isCompact && (
                        <div className="px-1.5 py-1 flex items-center justify-between text-[10px] text-slate-500 font-mono">
                          <span>#{c.number}</span>
                          {price && (
                            <span className="font-bold text-slate-900 truncate">
                              {format(price.value, price.currency)}
                            </span>
                          )}
                        </div>
                      )}
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <div className="text-slate-400 text-center py-20">
          {t("set_detail.not_found", "Série introuvable.")}
        </div>
      )}
    </div>
  );
}
